import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import markColour from './logos/barah-mark.svg';
import markWhite from './logos/barah-mark-white.svg';
import lockupArColour from './logos/barah-ar-color.svg';
import lockupArOnDark from './logos/barah-ar-color-on-dark.svg';
import lockupArWhite from './logos/barah-ar-white.svg';
import lockupEnColour from './logos/barah-en-color.svg';
import lockupEnOnDark from './logos/barah-en-color-on-dark.svg';
import lockupEnWhite from './logos/barah-en-white.svg';
import { BarahColors } from './tokens';

/**
 * The Barah brand lockup.
 *
 * Two rules from the corporate design guide shape this component. The corporate mark and the product name
 * are separate assets and must not be merged into one image — so the wordmark SVG is only ever the corporate
 * "Barah" lockup, and "Barah Pay" is live text. And the product name is never baked into an image, which is
 * why the Arabic and English names come from i18n rather than from two files to keep in sync.
 *
 * The SVGs are referenced as images rather than inlined: each one carries its own `<style>` block with
 * generic class names (`.cls-1`) and a gradient id, so inlining two of them on one page would have them
 * overwrite each other's fills.
 */
export type BrandTone = 'colour' | 'onDark';

export interface BarahBrandProps {
  /** `onDark` swaps to the white-on-navy artwork. */
  tone?: BrandTone;
  /** Height of the mark in pixels; everything else scales from it. */
  size?: number;
  /** Hide the product name and show the mark alone — for a collapsed sidebar. */
  markOnly?: boolean;
  /** Show the corporate lockup as a signature under the product name. */
  showVendor?: boolean;
  style?: CSSProperties;
  className?: string;
}

export function BarahBrand({
  tone = 'colour',
  size = 36,
  markOnly = false,
  showVendor = false,
  style,
  className,
}: BarahBrandProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');
  const onDark = tone === 'onDark';

  const mark = onDark ? markWhite : markColour;

  const lockup = isArabic
    ? onDark
      ? lockupArWhite
      : lockupArColour
    : onDark
      ? lockupEnWhite
      : lockupEnColour;

  return (
    <div
      className={className}
      style={{ display: 'flex', alignItems: 'center', gap: size * 0.3, minWidth: 0, ...style }}
    >
      <img src={mark} alt="" width={size} height={size} style={{ display: 'block', flexShrink: 0 }} />

      {markOnly ? null : (
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: size * 0.5,
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: isArabic ? 0 : '-0.01em',
              color: onDark ? '#FFFFFF' : BarahColors.navy,
              whiteSpace: 'nowrap',
            }}
          >
            {t('common.appName')}
          </span>

          {showVendor ? (
            <img
              src={lockup}
              alt=""
              style={{ display: 'block', height: size * 0.34, width: 'auto', opacity: onDark ? 0.85 : 0.7 }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * The corporate lockup on its own, for a footer or a "powered by" signature. `onDark` uses the artwork
 * drawn for dark panels rather than the flat white one, so the gradient survives.
 */
export function BarahWordmark({
  tone = 'colour',
  height = 18,
  style,
}: {
  tone?: BrandTone;
  height?: number;
  style?: CSSProperties;
}) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');

  const source = isArabic
    ? tone === 'onDark'
      ? lockupArOnDark
      : lockupArColour
    : tone === 'onDark'
      ? lockupEnOnDark
      : lockupEnColour;

  return <img src={source} alt="" style={{ display: 'block', height, width: 'auto', ...style }} />;
}
